import { useState } from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import noop from 'lodash/fp/noop';
import { withStyles } from '@material-ui/styles';

import * as trackingActions from 'dux/tracking/actions';
import { getUserSubscriptionDiscount } from 'dux/user/selectors';

import { srcOrNextImageProp } from 'PropTypes/imageProps';

import LazyImg from 'Components/LazyImg';
import Typography from 'Components/Typography';

const styles = theme => ({
  card: {
    color: 'inherit',
    display: 'flex',
    maxWidth: 280,
    flexDirection: 'column',
    alignItems: 'center',
    paddingBottom: theme.spacing.s40,
    '&.no-cta': {
      paddingBottom: 0,
    },
    '& > .description': {
      flexGrow: 1,
    },
    [theme.breakpoints.match('sm')]: {
      padding: 0,
      paddingBottom: theme.spacing.s40,
      width: '100%',
      maxWidth: 450,
      '&.no-cta:last-child': {
        paddingBottom: 0,
      },
    },
  },
  label: {
    position: 'relative',
    '& > p': {
      position: 'absolute',
      top: -4,
      left: -33,
      [theme.breakpoints.match('sm')]: {
        position: 'relative',
        textAlign: 'center',
        top: 'auto',
        left: 'auto',
      },
    },
  },
  imageContainer: {
    position: 'relative',
    width: 250,
    height: 330,
    marginBottom: theme.spacing.s24,
    '& > img': {
      position: 'absolute',
      left: 0,
      bottom: 0,
    },
    [theme.breakpoints.match('sm')]: {
      marginTop: theme.spacing.s24,
      position: 'inherit',
      width: 'auto',
      height: 'auto',
      maxWidth: 250,
      maxHeight: 350,
      '& > img': {
        position: 'inherit',
      },
    },
  },
  tag: {
    background: 'white',
    position: 'absolute',
    top: 12,
    left: 12,
    display: 'inline-block',
    height: '36px',
    lineHeight: '26px!important',
    padding: 8,
  },
});

const Tag = ({ content, classes }) => <Typography className={classes.tag}>{content}</Typography>;

Tag.propTypes = {
  classes: PropTypes.objectOf(PropTypes.string).isRequired,
  content: PropTypes.string.isRequired,
};

const ProductsItem = ({
  bgLabelledPicture,
  bgPicture,
  classes,
  capacity,
  capacityVariant,
  cardTag,
  dataClick,
  dataFrom,
  discount,
  displayCTA,
  isNew,
  label,
  labelVariant,
  price,
  productPageUrl,
  slug,
  trackHover,
}) => {
  const [isHovered, setHovered] = useState(false);
  const priceWithDiscount = (price * (1 - discount / 100)).toFixed(2);

  return (
    <Link
      className={classNames(classes.card, { 'no-cta': !displayCTA })}
      data-click={`${dataClick}-${slug}`}
      data-from={dataFrom}
      data-testid={`product-item-card-${slug}`}
      onMouseEnter={() => {
        setHovered(true);
        trackHover({ dataFrom, dataClick, productSlug: slug });
      }}
      onMouseLeave={() => setHovered(false)}
      to={productPageUrl}
    >
      <div className={classes.imageContainer}>
        <LazyImg alt={label} src={isHovered ? bgLabelledPicture : bgPicture} width="100%" />
      </div>

      <Typography
        className={classes.label}
        markupName={isNew ? 'div' : 'p'}
        paragraph
        variant={labelVariant}
      >
        {isNew && (
          <Typography color="sorbet" markupName="p" paragraph variant="mono2">
            NEW
          </Typography>
        )}
        {label}
      </Typography>

      <Typography color="noir" variant={capacityVariant}>
        From ${priceWithDiscount}, {capacity}
      </Typography>

      {cardTag && <Tag classes={classes} content={cardTag} />}
    </Link>
  );
};

ProductsItem.propTypes = {
  bgLabelledPicture: srcOrNextImageProp.isRequired,
  bgPicture: srcOrNextImageProp.isRequired,
  capacity: PropTypes.string.isRequired,
  capacityVariant: PropTypes.string,
  cardTag: PropTypes.string,
  classes: PropTypes.objectOf(PropTypes.string).isRequired,
  dataClick: PropTypes.string,
  dataFrom: PropTypes.string,
  discount: PropTypes.number.isRequired,
  displayCTA: PropTypes.bool,
  isNew: PropTypes.bool,
  label: PropTypes.string.isRequired,
  labelVariant: PropTypes.string,
  price: PropTypes.number.isRequired,
  productPageUrl: PropTypes.string.isRequired,
  slug: PropTypes.string.isRequired,
  trackHover: PropTypes.func,
};

ProductsItem.defaultProps = {
  capacityVariant: 'p2',
  cardTag: null,
  dataClick: 'see-product',
  dataFrom: 'products-block',
  displayCTA: false,
  isNew: false,
  labelVariant: 'h3',
  trackHover: noop,
};

const StyledProductsItem = withStyles(styles)(ProductsItem);

export default connect(
  state => ({
    discount: getUserSubscriptionDiscount(state),
  }),
  {
    trackHover: trackingActions.trackHover,
  }
)(StyledProductsItem);
